import { collection, documentId, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
    EvaluationCollaborator,
    EvaluationInviteTracking,
    EvaluationInviteType,
    PerformanceEvaluationCampaign,
    PerformanceEvaluationResponse,
    RESPONSE_COLLECTION,
    getPerformanceEvaluationCampaignById,
    getPerformanceEvaluationCollaborators,
} from './evaluationsService';

export type EvaluationOverviewStatus = 'missing' | 'not_sent' | 'sent' | 'opened' | 'completed' | 'unavailable';
export type EvaluationGroupColor = 'green' | 'yellow' | 'red' | 'gray';

export interface EvaluationSummaryMetric {
    total: number;
    byType: Record<EvaluationInviteType, number>;
}

export interface EvaluationEvaluatorCard {
    type: EvaluationInviteType;
    title: string;
    status: EvaluationOverviewStatus;
    statusLabel: string;
    recipientName: string | null;
    recipientEmail: string | null;
    photoUrl?: string | null;
    sentAt?: Date;
    openedAt?: Date;
    completedAt?: Date;
    responseId?: string | null;
    tokenId?: string | null;
    canResend: boolean;
    canCopyLink: boolean;
    trackingAvailable: boolean;
    helperText?: string;
}

export interface EvaluationCollaboratorGroup {
    collaboratorId: string;
    collaboratorName: string;
    collaboratorEmail?: string;
    color: EvaluationGroupColor;
    cards: EvaluationEvaluatorCard[];
}

export interface EvaluationCampaignOverview {
    campaign: PerformanceEvaluationCampaign;
    summary: {
        sent: EvaluationSummaryMetric;
        opened: EvaluationSummaryMetric;
        completed: EvaluationSummaryMetric;
        pending: EvaluationSummaryMetric;
        unavailable: EvaluationSummaryMetric;
        totalExpected: number;
    };
    collaboratorGroups: EvaluationCollaboratorGroup[];
}

const emptyMetric = (): EvaluationSummaryMetric => ({
    total: 0,
    byType: {
        SELF: 0,
        LEADER: 0,
        CLIENT: 0,
    },
});

const inviteKeyByType: Record<EvaluationInviteType, keyof NonNullable<EvaluationCollaborator['invites']>> = {
    SELF: 'self',
    LEADER: 'leader',
    CLIENT: 'client',
};

const cardTitleByType: Record<EvaluationInviteType, string> = {
    SELF: 'Colaborador',
    LEADER: 'Responsável',
    CLIENT: 'Cliente',
};

const missingCopyByType: Record<Exclude<EvaluationInviteType, 'SELF'>, string> = {
    LEADER: 'Sem responsável vinculado',
    CLIENT: 'Sem cliente vinculado',
};

function getRecipientSeed(collaborator: EvaluationCollaborator, type: EvaluationInviteType) {
    if (type === 'SELF') {
        return {
            hasBinding: true,
            name: collaborator.collaboratorName || 'Colaborador',
            email: collaborator.collaboratorEmail || null,
            photoUrl: collaborator.collaboratorPhotoUrl || null,
        };
    }

    if (type === 'LEADER') {
        const hasBinding = Boolean(collaborator.responsibleId || collaborator.responsibleName || collaborator.responsibleEmail);
        return {
            hasBinding,
            name: collaborator.responsibleName || null,
            email: collaborator.responsibleEmail || null,
            photoUrl: collaborator.responsiblePhotoUrl || null,
        };
    }

    const hasBinding = Boolean(collaborator.clientResponsibleName || collaborator.clientResponsibleEmail);
    return {
        hasBinding,
        name: collaborator.clientResponsibleName || null,
        email: collaborator.clientResponsibleEmail || null,
        photoUrl: null,
    };
}

function deriveCardStatus(params: {
    campaign: PerformanceEvaluationCampaign;
    collaborator: EvaluationCollaborator;
    type: EvaluationInviteType;
    invite?: EvaluationInviteTracking;
    completedLegacySet: Set<string>;
}): EvaluationEvaluatorCard {
    const { campaign, collaborator, type, invite, completedLegacySet } = params;
    const title = cardTitleByType[type];
    const recipient = getRecipientSeed(collaborator, type);

    if (!recipient.hasBinding) {
        return {
            type,
            title,
            status: 'missing',
            statusLabel: 'Não configurado',
            recipientName: null,
            recipientEmail: null,
            canResend: false,
            canCopyLink: false,
            trackingAvailable: false,
            helperText: missingCopyByType[type as Exclude<EvaluationInviteType, 'SELF'>],
        };
    }

    const recipientName = invite?.recipientName || recipient.name || null;
    const recipientEmail = invite?.recipientEmail || recipient.email || null;
    const photoUrl = recipient.photoUrl || null;
    const legacyCompleted = completedLegacySet.has(`${collaborator.collaboratorId}:${type}`);
    const isDraftCampaign = campaign.status === 'draft';
    const trackingAvailable = Boolean(invite);

    if (invite?.completedAt || legacyCompleted) {
        return {
            type,
            title,
            status: 'completed',
            statusLabel: 'Concluído',
            recipientName,
            recipientEmail,
            photoUrl,
            sentAt: invite?.sentAt,
            openedAt: invite?.openedAt,
            completedAt: invite?.completedAt,
            responseId: invite?.responseId || null,
            tokenId: invite?.tokenId || null,
            canResend: false,
            canCopyLink: false,
            trackingAvailable,
            helperText: legacyCompleted && !invite?.completedAt ? 'Conclusão inferida por resposta existente.' : undefined,
        };
    }

    if (invite?.openedAt) {
        return {
            type,
            title,
            status: 'opened',
            statusLabel: 'Aberto',
            recipientName,
            recipientEmail,
            photoUrl,
            sentAt: invite.sentAt,
            openedAt: invite.openedAt,
            completedAt: invite.completedAt,
            responseId: invite.responseId || null,
            tokenId: invite.tokenId || null,
            canResend: campaign.status === 'active' && Boolean(recipientEmail),
            canCopyLink: campaign.status === 'active' && Boolean(recipientEmail),
            trackingAvailable,
        };
    }

    if (invite?.sentAt || invite?.lastSentAt) {
        return {
            type,
            title,
            status: 'sent',
            statusLabel: 'Enviado',
            recipientName,
            recipientEmail,
            photoUrl,
            sentAt: invite.sentAt || invite.lastSentAt,
            openedAt: invite.openedAt,
            completedAt: invite.completedAt,
            responseId: invite.responseId || null,
            tokenId: invite.tokenId || null,
            canResend: campaign.status === 'active' && Boolean(recipientEmail),
            canCopyLink: campaign.status === 'active' && Boolean(recipientEmail),
            trackingAvailable,
        };
    }

    if (invite || isDraftCampaign) {
        return {
            type,
            title,
            status: 'not_sent',
            statusLabel: 'Não enviado',
            recipientName,
            recipientEmail,
            photoUrl,
            responseId: invite?.responseId || null,
            tokenId: invite?.tokenId || null,
            canResend: campaign.status === 'active' && Boolean(recipientEmail),
            canCopyLink: campaign.status === 'active' && Boolean(recipientEmail),
            trackingAvailable,
        };
    }

    return {
        type,
        title,
        status: 'unavailable',
        statusLabel: 'Sem histórico',
        recipientName,
        recipientEmail,
        photoUrl,
        responseId: null,
        tokenId: null,
        canResend: campaign.status === 'active' && Boolean(recipientEmail),
        canCopyLink: campaign.status === 'active' && Boolean(recipientEmail),
        trackingAvailable: false,
        helperText: 'Campanha antiga sem rastreamento de envio/abertura.',
    };
}

function resolveGroupColor(campaign: PerformanceEvaluationCampaign, cards: EvaluationEvaluatorCard[]): EvaluationGroupColor {
    const activeCards = cards.filter((card) => card.status !== 'missing');

    if (activeCards.length === 0) {
        return 'gray';
    }

    if (activeCards.some((card) => card.status === 'unavailable')) {
        return 'gray';
    }

    if (activeCards.every((card) => card.status === 'completed')) {
        return 'green';
    }

    if (campaign.status === 'draft' || activeCards.every((card) => card.status === 'not_sent')) {
        return 'gray';
    }

    if (activeCards.some((card) => card.status === 'not_sent' || card.status === 'sent')) {
        return 'red';
    }

    if (activeCards.every((card) => card.status === 'opened' || card.status === 'completed')) {
        return 'yellow';
    }

    return 'gray';
}

export function buildEvaluationCampaignOverview(params: {
    campaign: PerformanceEvaluationCampaign;
    collaborators: EvaluationCollaborator[];
    responses: PerformanceEvaluationResponse[];
}): EvaluationCampaignOverview {
    const { campaign, collaborators, responses } = params;
    const completedLegacySet = new Set(
        responses
            .filter((response) => response.status === 'completed')
            .map((response) => `${response.collaboratorId}:${response.type}`)
    );

    const summary = {
        sent: emptyMetric(),
        opened: emptyMetric(),
        completed: emptyMetric(),
        pending: emptyMetric(),
        unavailable: emptyMetric(),
        totalExpected: 0,
    };

    const collaboratorGroups = [...collaborators]
        .sort((left, right) => left.collaboratorName.localeCompare(right.collaboratorName))
        .map((collaborator) => {
            const cards = (['SELF', 'LEADER', 'CLIENT'] as EvaluationInviteType[]).map((type) => {
                const invite = collaborator.invites?.[inviteKeyByType[type]];
                return deriveCardStatus({
                    campaign,
                    collaborator,
                    type,
                    invite,
                    completedLegacySet,
                });
            });

            cards.forEach((card) => {
                if (card.status === 'missing') {
                    return;
                }

                summary.totalExpected += 1;

                if (card.status === 'completed') {
                    summary.completed.total += 1;
                    summary.completed.byType[card.type] += 1;
                    return;
                }

                if (card.status === 'opened') {
                    summary.opened.total += 1;
                    summary.opened.byType[card.type] += 1;
                    return;
                }

                if (card.status === 'sent') {
                    summary.sent.total += 1;
                    summary.sent.byType[card.type] += 1;
                    return;
                }

                if (card.status === 'unavailable') {
                    summary.unavailable.total += 1;
                    summary.unavailable.byType[card.type] += 1;
                    return;
                }

                summary.pending.total += 1;
                summary.pending.byType[card.type] += 1;
            });

            return {
                collaboratorId: collaborator.collaboratorId,
                collaboratorName: collaborator.collaboratorName,
                collaboratorEmail: collaborator.collaboratorEmail,
                color: resolveGroupColor(campaign, cards),
                cards,
            };
        });

    return {
        campaign,
        summary,
        collaboratorGroups,
    };
}

async function hydrateCollaboratorPhotos(collaborators: EvaluationCollaborator[]): Promise<EvaluationCollaborator[]> {
    const missingIds = Array.from(new Set(
        collaborators.flatMap((collaborator) => {
            const ids: string[] = [];

            if (!collaborator.collaboratorPhotoUrl && collaborator.collaboratorId) {
                ids.push(collaborator.collaboratorId);
            }

            if (!collaborator.responsiblePhotoUrl && collaborator.responsibleId) {
                ids.push(collaborator.responsibleId);
            }

            return ids;
        })
    ));

    if (missingIds.length === 0) {
        return collaborators;
    }

    const photoByCollaboratorId = new Map<string, string | null>();
    const chunkSize = 10;

    for (let index = 0; index < missingIds.length; index += chunkSize) {
        const chunk = missingIds.slice(index, index + chunkSize);
        const snapshot = await getDocs(query(
            collection(db, 'collaborators_public'),
            where(documentId(), 'in', chunk)
        ));

        snapshot.docs.forEach((doc) => {
            const data = doc.data();
            photoByCollaboratorId.set(doc.id, data.photoUrl ?? null);
        });
    }

    return collaborators.map((collaborator) => ({
        ...collaborator,
        collaboratorPhotoUrl: collaborator.collaboratorPhotoUrl
            ?? photoByCollaboratorId.get(collaborator.collaboratorId)
            ?? null,
        responsiblePhotoUrl: collaborator.responsiblePhotoUrl
            ?? (collaborator.responsibleId ? photoByCollaboratorId.get(collaborator.responsibleId) : null)
            ?? null,
    }));
}

export const getEvaluationCampaignOverview = async (campaignId: string): Promise<EvaluationCampaignOverview | null> => {
    const [campaign, collaborators, responsesSnapshot] = await Promise.all([
        getPerformanceEvaluationCampaignById(campaignId),
        getPerformanceEvaluationCollaborators(campaignId),
        getDocs(query(
            collection(db, RESPONSE_COLLECTION),
            where('campaignId', '==', campaignId),
            where('status', '==', 'completed')
        )),
    ]);

    if (!campaign) {
        return null;
    }

    const collaboratorsWithPhotos = await hydrateCollaboratorPhotos(collaborators);
    const responses = responsesSnapshot.docs.map((responseDoc) => ({
        id: responseDoc.id,
        ...responseDoc.data(),
    } as PerformanceEvaluationResponse));

    return buildEvaluationCampaignOverview({
        campaign,
        collaborators: collaboratorsWithPhotos,
        responses,
    });
};
